import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin } from "lucide-react";

// One shared fetch per page load; callers get a fresh copy after `refreshBranches()`.
let _cache = null;
let _inflight = null;
const _subs = new Set();

export function refreshBranches() {
  _inflight = api.get("/branches").then((r) => { _cache = r.data; _subs.forEach((f) => f(_cache)); return _cache; }).catch(() => []).finally(() => { _inflight = null; });
  return _inflight;
}

export function useBranches() {
  const { user } = useAuth();
  const [branches, setBranches] = useState(_cache || []);
  useEffect(() => {
    if (!user || user.role === "client") return undefined;
    _subs.add(setBranches);
    if (!_cache && !_inflight) refreshBranches();
    else if (_cache) setBranches(_cache);
    return () => { _subs.delete(setBranches); };
  }, [user]);
  const byId = Object.fromEntries(branches.map((b) => [b.id, b]));
  return { branches, byId };
}

/** Small "Bengaluru" pill; renders "Company-wide" for records with no branch. */
export function BranchTag({ id, byId, className = "" }) {
  const b = id ? byId?.[id] : null;
  return (
    <Badge variant="outline" className={`gap-1 text-[10px] font-normal ${className}`} data-testid="branch-tag">
      <MapPin className="w-3 h-3" />
      {b ? (b.city || b.name) : (id ? "Branch" : "Company-wide")}
      {b?.is_head_office && <span className="text-primary">· HO</span>}
    </Badge>
  );
}

/** Branch picker — only the Company Admin chooses; Branch Admins are fixed to their own branch. */
export function BranchSelect({ value, onChange, label = "Branch" }) {
  const { user } = useAuth();
  const { branches } = useBranches();
  if (user?.role !== "admin") return null;
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
        <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Company-wide" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">Company-wide (no branch)</SelectItem>
          {branches.filter((b) => b.is_active !== false).map((b) => (
            <SelectItem key={b.id} value={b.id}>{b.name}{b.city ? ` · ${b.city}` : ""}{b.is_head_office ? " (Head Office)" : ""}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
